import React, { useState } from 'react';
import { X, Sparkles, Image as ImageIcon, RefreshCw } from 'lucide-react';
import RichTextEditor from '../components/RichTextEditor';
import '../components/RichTextEditor.css';
import {
  canSaveArticle,
  getArticleContentState,
  hasMeaningfulArticleContent,
} from '../../utils/articleContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ArticleFormModal({ article, onSave, onClose, contentLoaded = true, saving = false }) {
  const [form, setForm] = useState(article || {
    title: '',
    excerpt: '',
    content: '',
    category: 'Santé',
    author: 'Centre Médical la Trinité',
    image_url: '',
    published: true,
  });

  // Text AI
  const [showAI, setShowAI] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiTone, setAiTone] = useState('bienveillant et pédagogique');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');

  // Image AI
  const [imgLoading, setImgLoading] = useState(false);
  const [imgError, setImgError] = useState('');

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleGenerateAI = async () => {
    if (aiTopic.trim().length < 3) {
      setAiError('Précisez un sujet (au moins 3 caractères).');
      return;
    }
    setAiError('');
    setAiLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/journal/generate-ai`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ topic: aiTopic.trim(), tone: aiTone.trim() || 'bienveillant et pédagogique' }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'La génération a échoué.');
      }
      const data = await res.json();
      setForm(prev => ({
        ...prev,
        title: data.title || prev.title,
        excerpt: data.excerpt || prev.excerpt,
        content: data.content || prev.content,
        category: data.category || prev.category,
      }));
      setShowAI(false);
      setAiTopic('');
    } catch (e) {
      setAiError(e.message || 'Erreur inconnue.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!form.title.trim() || form.title.trim().length < 3) {
      setImgError("Renseignez d'abord le titre de l'article.");
      return;
    }
    setImgError('');
    setImgLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/journal/generate-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: form.title.trim(),
          category: (form.category || 'Santé').trim(),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'La génération de l\'image a échoué.');
      }
      const data = await res.json();
      if (!data.image_url) throw new Error('Aucune image renvoyée.');
      // Cache-buster to force preview refresh on regeneration
      setForm(prev => ({ ...prev, image_url: data.image_url }));
    } catch (e) {
      setImgError(e.message || 'Erreur inconnue.');
    } finally {
      setImgLoading(false);
    }
  };

  const contentIsMeaningful = hasMeaningfulArticleContent(form.content);
  const isExistingArticle = !!article?.article_id;
  const contentState = getArticleContentState({
    isExistingArticle,
    contentLoaded,
    content: form.content,
  });
  const contentWasNotLoaded = contentState === 'not-loaded';
  const loadedContentIsEmpty = isExistingArticle && contentState === 'loaded-empty';
  const saveIsAllowed = canSaveArticle({
    title: form.title,
    excerpt: form.excerpt,
    content: form.content,
    isExistingArticle,
    contentLoaded,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (contentWasNotLoaded) {
      alert("Le contenu complet n'est pas chargé. Enregistrement bloqué par sécurité.");
      return;
    }
    if (!form.title.trim() || !form.excerpt.trim() || !contentIsMeaningful) {
      alert('Titre, chapô et contenu sont obligatoires.');
      return;
    }
    onSave({
      title: form.title,
      excerpt: form.excerpt,
      content: form.content,
      category: form.category,
      author: form.author,
      image_url: form.image_url || '',
      published: !!form.published,
    });
  };

  const inputStyle = { padding: 12, borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, width: '100%', fontFamily: 'inherit' };

  // Compose preview URL: relative /api/... → prepend API_URL, absolute → as-is
  const previewSrc = form.image_url
    ? (form.image_url.startsWith('/api') ? `${API_URL}${form.image_url}` : form.image_url)
    : '';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720, width: '95%', maxHeight: '92vh', overflowY: 'auto' }} data-testid="article-form-modal">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0 }}>{article ? "Modifier l'article" : 'Nouvel article'}</h2>
          <button onClick={onClose} disabled={saving} data-testid="article-form-close-button" style={{ background: 'none', border: 'none', cursor: saving ? 'not-allowed' : 'pointer' }}><X size={22} /></button>
        </div>

        {/* AI text generation panel */}
        {!showAI ? (
          <button
            type="button"
            onClick={() => setShowAI(true)}
            data-testid="article-ai-open-btn"
            style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14,
              padding: '10px 14px', borderRadius: 10,
              background: 'linear-gradient(135deg, #1565C0, #0d47a1)',
              color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14,
            }}
          >
            <Sparkles size={18} /> Rédiger avec l'IA
          </button>
        ) : (
          <div data-testid="article-ai-panel" style={{
            marginBottom: 16, padding: 14, borderRadius: 10,
            background: '#eff6ff', border: '1px solid #bfdbfe',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <strong style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#1e40af' }}>
                <Sparkles size={16} /> Assistant de rédaction (GPT-5.2)
              </strong>
              <button type="button" onClick={() => { setShowAI(false); setAiError(''); }} data-testid="article-ai-close-btn" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                <X size={16} />
              </button>
            </div>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 8 }}>Sujet de l'article
              <input
                value={aiTopic}
                onChange={(e) => setAiTopic(e.target.value)}
                placeholder="Ex. : Prévention de la drépanocytose chez l'enfant en RDC"
                data-testid="article-ai-topic"
                style={{ ...inputStyle, marginTop: 4 }}
                disabled={aiLoading}
              />
            </label>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 10 }}>Ton (optionnel)
              <input
                value={aiTone}
                onChange={(e) => setAiTone(e.target.value)}
                placeholder="bienveillant et pédagogique"
                data-testid="article-ai-tone"
                style={{ ...inputStyle, marginTop: 4 }}
                disabled={aiLoading}
              />
            </label>
            {aiError && (
              <div data-testid="article-ai-error" style={{ padding: 8, marginBottom: 10, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, color: '#991b1b', fontSize: 13 }}>
                {aiError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                type="button"
                onClick={handleGenerateAI}
                disabled={aiLoading}
                data-testid="article-ai-generate-btn"
                style={{
                  padding: '8px 16px', borderRadius: 8,
                  background: aiLoading ? '#94a3b8' : '#1565C0',
                  color: '#fff', border: 'none', cursor: aiLoading ? 'wait' : 'pointer', fontWeight: 600, fontSize: 14,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <Sparkles size={16} /> {aiLoading ? 'Génération en cours (30-60 s)…' : 'Générer le brouillon'}
              </button>
              <span style={{ fontSize: 12, color: '#64748b' }}>
                {aiLoading ? 'Patientez, GPT-5.2 rédige…' : 'Le contenu généré remplacera le formulaire ci-dessous.'}
              </span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 600 }}>Titre *
            <input required value={form.title} onChange={(e) => set('title', e.target.value)} style={inputStyle} data-testid="article-title" placeholder="Titre de l'article" />
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label style={{ fontSize: 13, fontWeight: 600 }}>Catégorie
              <input value={form.category} onChange={(e) => set('category', e.target.value)} style={inputStyle} data-testid="article-category" placeholder="Prévention, Actualité, Éditorial…" />
            </label>
            <label style={{ fontSize: 13, fontWeight: 600 }}>Auteur
              <input value={form.author} onChange={(e) => set('author', e.target.value)} style={inputStyle} data-testid="article-author" />
            </label>
          </div>

          {/* Image field with AI generation */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <label style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>Image de couverture</label>
              <button
                type="button"
                onClick={handleGenerateImage}
                disabled={imgLoading}
                data-testid="article-ai-image-btn"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 12px', borderRadius: 8,
                  background: imgLoading ? '#94a3b8' : 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                  color: '#fff', border: 'none', cursor: imgLoading ? 'wait' : 'pointer',
                  fontWeight: 600, fontSize: 13,
                }}
                title="Générer une photo réaliste avec Nano Banana à partir du titre"
              >
                {imgLoading
                  ? <><RefreshCw size={14} className="spin" /> Génération photo…</>
                  : (form.image_url ? <><RefreshCw size={14} /> Regénérer avec l'IA</> : <><ImageIcon size={14} /> Générer avec l'IA</>)}
              </button>
            </div>
            <input
              value={form.image_url}
              onChange={(e) => set('image_url', e.target.value)}
              style={inputStyle}
              data-testid="article-image-url"
              placeholder="Colle une URL ou clique sur « Générer avec l'IA »"
            />
            {imgError && (
              <div data-testid="article-image-error" style={{ padding: 8, marginTop: 6, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, color: '#991b1b', fontSize: 13 }}>
                {imgError}
              </div>
            )}
            {imgLoading && (
              <div data-testid="article-image-loading" style={{ marginTop: 8, padding: 12, background: '#f5f3ff', border: '1px dashed #c4b5fd', borderRadius: 8, color: '#5b21b6', fontSize: 13, textAlign: 'center' }}>
                Nano Banana produit une photographie éditoriale (20-40 s)…
              </div>
            )}
            {previewSrc && !imgLoading && (
              <div style={{ marginTop: 8 }}>
                <img
                  src={previewSrc}
                  alt="Aperçu de la couverture"
                  data-testid="article-image-preview"
                  onError={(e) => { e.target.style.display = 'none'; }}
                  style={{ width: '100%', maxHeight: 260, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }}
                />
                <p style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                  Aperçu — l'image sera affichée en tête de l'article publié.
                </p>
              </div>
            )}
          </div>

          <label style={{ fontSize: 13, fontWeight: 600 }}>Chapô (résumé court) *
            <textarea required rows={3} value={form.excerpt} onChange={(e) => set('excerpt', e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} data-testid="article-excerpt" placeholder="2–3 phrases qui donnent envie de lire l'article." />
          </label>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>
              Contenu de l'article *
            </label>
            {contentWasNotLoaded && (
              <div role="alert" data-testid="article-content-not-loaded" style={{ padding: 10, marginBottom: 8, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 13 }}>
                Contenu non chargé — enregistrement désactivé pour protéger l'article existant.
              </div>
            )}
            {loadedContentIsEmpty && (
              <div role="alert" data-testid="article-content-empty-warning" style={{ padding: 10, marginBottom: 8, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, color: '#92400e', fontSize: 13 }}>
                Contenu chargé mais vide — saisissez un contenu réel avant d'enregistrer.
              </div>
            )}
            <RichTextEditor
              value={form.content}
              onChange={(html) => set('content', html)}
            />
            <p style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
              Utilisez la barre d'outils pour mettre en forme votre article (titres, gras, listes, citations, liens…).
            </p>
          </div>
          <label style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={!!form.published} onChange={(e) => set('published', e.target.checked)} data-testid="article-published" />
            Publier (visible sur le site public)
          </label>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 6 }}>
            <button type="button" onClick={onClose} disabled={saving} data-testid="article-form-cancel-button" style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', cursor: saving ? 'not-allowed' : 'pointer' }}>Annuler</button>
            <button
              type="submit"
              className="btn-primary"
              disabled={saving || !saveIsAllowed}
              data-testid="article-save-btn"
              style={{ padding: '8px 18px', opacity: saving || !saveIsAllowed ? 0.55 : 1, cursor: saving || !saveIsAllowed ? 'not-allowed' : 'pointer' }}
            >
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ArticleFormModal;
